import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Amount, ServiceCatalogTranslations } from '@fcom/dapi';
import { ButtonTheme, IconButtonSize, IconButtonTheme, ModalButtons, ToggleSwitchStatus } from '@fcom/ui-components';
import { asTravelerService } from '@fcom/common-booking/store/utils/services.utils';
import { ElementActions, ElementTypes, LoaderType } from '@fcom/common/interfaces';
import { Category } from '@fcom/dapi/api/models';
import { GtmService } from '@fcom/common/gtm';
import { LoaderTheme } from '@fcom/ui-components/components/loader/enums';
import { CmsImageData } from '@fcom/core-api/interfaces';

import {
  ServiceCatalogServiceWithSelection,
  PaxServiceCategoryPerFragmentWithSelections,
  ServiceSelectionChange,
} from '../../interfaces/pax-ancillaries.interface';

interface ServiceInfo {
  title?: string;
  url: string;
}

@Component({
  selector: 'fin-pax-ancillary-product',
  templateUrl: './pax-ancillary-product.component.html',
  styleUrls: ['./pax-ancillary-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaxAncillaryProductComponent implements OnInit {
  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconButtonSize = IconButtonSize;
  readonly LoaderType = LoaderType;
  readonly Category = Category;
  readonly ToggleSwitchStatus = ToggleSwitchStatus;
  readonly ModalButtons = ModalButtons;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input({ required: true })
  categoryWithSelections$: Observable<PaxServiceCategoryPerFragmentWithSelections>;

  @Input({ required: true })
  category: Category;

  @Input({ required: true })
  price$: Observable<Amount>;

  @Input({ required: true })
  servicesReady$: Observable<boolean>;

  @Input({ required: true })
  translations: ServiceCatalogTranslations;

  @Input({ required: true })
  loadingTitle: string;

  @Input() customProductTitle?: string;

  @Input()
  isLoading$: Observable<boolean>;

  @Input()
  media?: CmsImageData;

  @Input()
  icon?: SvgLibraryIcon;

  @Input()
  actionTextSubTitle?: string;

  @Input()
  showInfoButton = true;

  @Input()
  analyticsContext = 'booking-flow_pax-details';

  @Output()
  selectionChanged = new EventEmitter<ServiceSelectionChange>();

  serviceInfo: ServiceInfo;
  zeroPrice$: Observable<Amount>;
  infoModalOpen = false;

  constructor(private gtmService: GtmService) {}

  ngOnInit(): void {
    this.serviceInfo = this.getServiceInfo();
    this.zeroPrice$ = this.price$.pipe(
      map((price) => {
        return { amount: '0.00', currencyCode: price.currencyCode };
      })
    );
  }

  updateServiceSelection(selected: boolean, service: ServiceCatalogServiceWithSelection, fragmentId?: string): void {
    const selection = selected ? asTravelerService(service) : undefined;
    this.selectionChanged.emit({ category: service.category, selection, fragmentId });
    const state = `${service.category}-${selected ? 'added' : 'removed'}`;

    this.gtmAnalytic(state);
  }

  openInfoModal(): void {
    this.infoModalOpen = true;
    this.gtmAnalytic('more-info');
  }

  getServiceInfo(): ServiceInfo {
    return { url: `fragments.services.productInfo.${this.category}.url` };
  }

  getFirstValue(
    categoryWithSelections: PaxServiceCategoryPerFragmentWithSelections | null
  ): ServiceCatalogServiceWithSelection | null {
    const firstKey = Object.keys(categoryWithSelections || {})[0];
    return categoryWithSelections?.[firstKey]?.[0] || null;
  }

  private gtmAnalytic(state: string): void {
    this.gtmService.trackElement(
      'pax-ancillaries',
      this.analyticsContext,
      ElementTypes.BUTTON,
      state,
      ElementActions.CLICK
    );
  }
}
